import React from 'react';
import {
  Alert, Box, Container, Grid, IconButton, Tooltip, Typography,
} from '@mui/material';
import { TripRequest } from '@marageti/z4-sdk/lib/travel';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { Ticket } from '@marageti/z4-sdk/lib/tickets';
import {
  ContextMenuItemType, InfiniteScroll, OutlinedIcons, ChipSelectList,
} from '@marageti/z4-lib';
import { ConversationWithTripDetails } from '@marageti/z4-sdk/lib/travel/Conversation';
import SideBar from './my-queue-sidebar';
import {
  BucketCounts, inboxNameToType, ListName, SortDirection, TravelerTag,
} from './my-queue-utils';
import InboxItemCard from './inbox-item-card';
import DateSeparatedList from './date-separated-list';
import ReminderCard from '../../../components/list-cards/reminder-cards';

const {
  OutlinedSortAscendingIcon,
  OutlinedSortDescendingIcon,
  OutlinedCheckCircleIcon,
} = OutlinedIcons;

type AssigneeOption = {
  title: string;
  value: string;
  status?: string;
};

type TripRequestItem = {
  tripRequest: TripRequest;
  contextMenuItems: ContextMenuItemType[] | undefined;
};

type MyQueueItem = ConversationWithTripDetails | TripRequestItem;

type MyLeadsViewProps = {
  error?: ApiError,
  bucketCounts: BucketCounts | undefined,
  hasMoreItems: boolean,
  isLoading: boolean,
  items?: MyQueueItem[],
  loadMore: () => void,
  onChangeFilter: (name: ListName) => void,
  selectedList: ListName,
  onTravelerTagChange: (tag: TravelerTag) => void,
  onSortDirectionChange: (direction: SortDirection) => void,
  selectedTag: TravelerTag,
  sortDirection: SortDirection,
  onDismissConversation: (tripRequestId: string) => void,
  // Reminders props
  reminders?: Ticket[],
  remindersTotalHits?: number,
  assignees?: AssigneeOption[],
  reschedulingTicketId?: string,
  togglingCompleteTicketId?: string,
  onEditTicket?: (ticket: Ticket) => void,
  onDuplicateTicket?: (ticket: Ticket) => void,
  onRescheduleTicket?: (ticket: Ticket) => void,
  onToggleComplete?: (ticket: Ticket) => void,
  onDismissTicket?: (ticket: Ticket) => void,
};

const MyLeadsView = ({
  error = undefined,
  bucketCounts,
  hasMoreItems,
  isLoading,
  items = [],
  loadMore,
  onChangeFilter,
  selectedList,
  onTravelerTagChange,
  onSortDirectionChange,
  selectedTag,
  sortDirection,
  onDismissConversation,
  // Reminders props
  reminders = [],
  remindersTotalHits = 0,
  assignees = [],
  reschedulingTicketId,
  togglingCompleteTicketId,
  onEditTicket,
  onDuplicateTicket,
  onRescheduleTicket,
  onToggleComplete,
  onDismissTicket,
}: MyLeadsViewProps) => {
  const isTravelerInbox = selectedList === 'Traveler Inbox';
  const isPartnerInbox = selectedList === 'Partner Inbox';
  const isInboxSection = isTravelerInbox || isPartnerInbox;
  const isRemindersSection = selectedList === 'Reminders';

  const inboxItems = isInboxSection
    ? items as ConversationWithTripDetails[]
    : [];

  const tripRequestItems = !isInboxSection
    ? items as TripRequestItem[]
    : [];

  const travelerTagChipOptions = [
    { key: 'RPT', label: 'RPT Traveler', value: 'Repeat' },
    { key: 'VIP', label: 'VIP Traveler', value: 'VIP' },
    { key: 'FOBY', label: 'FOBY Traveler', value: 'FOBY' },
  ];

  const selectedChips = selectedTag !== 'All' ? [selectedTag] : [];

  const handleChipChange = (chips: string[]) => {
    if (chips.length === 0) {
      onTravelerTagChange('All');
      return;
    }
    const lastSelected = chips[chips.length - 1];
    onTravelerTagChange(lastSelected as TravelerTag);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{
        my: {
          desktop: 6,
          mobile: 3,
        },
      }}
      >
        <Grid
          container
          columnSpacing={5.5}
          rowSpacing={3}
        >
          <Grid
            item
            mobile={12}
            desktop={2.2}
          >
            <Typography variant="h2" sx={{ mb: 3 }}>My Queue</Typography>
            <SideBar
              bucketCounts={bucketCounts}
              onChange={onChangeFilter}
              selectedItem={selectedList}
            />
          </Grid>
          <Grid
            item
            mobile={12}
            desktop={9.8}
          >
            {isInboxSection && (
              <Box mb={2} display="flex" justifyContent="space-between" alignItems="center" flexDirection="row-reverse">
                <Tooltip title={sortDirection === 'asc' ? 'Oldest messages first' : 'Newest messages first'}>
                  <IconButton onClick={() => onSortDirectionChange(sortDirection === 'asc' ? 'desc' : 'asc')} color="primary">
                    {sortDirection === 'asc' ? <OutlinedSortAscendingIcon /> : <OutlinedSortDescendingIcon />}
                  </IconButton>
                </Tooltip>
                <Box display={isPartnerInbox ? 'none' : 'inherit'}>
                  <ChipSelectList
                    chipOptions={travelerTagChipOptions}
                    selectedChips={selectedChips}
                    onChange={handleChipChange}
                  />
                </Box>
              </Box>
            )}
            {isRemindersSection ? (
              <>
                <Typography variant="h4" sx={{ mb: 2 }}>
                  {`${remindersTotalHits} Results`}
                </Typography>
                {!isLoading && reminders.length === 0 ? (
                  <Alert
                    severity="success"
                    icon={<OutlinedCheckCircleIcon />}
                  >
                    <Typography variant="bodyBold" color="text.primary">
                      You&apos;re All Caught Up!
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      There are no pending reminders needing your attention right now.
                    </Typography>
                  </Alert>
                ) : (
                  <InfiniteScroll
                    error={error}
                    hasItems={reminders.length > 0}
                    hasMoreItems={hasMoreItems}
                    isLoading={isLoading}
                    loadMoreItems={loadMore}
                    stackProps={{ spacing: 1 }}
                  >
                    {reminders.map((ticket) => (
                      <ReminderCard
                        key={ticket.id}
                        ticket={ticket}
                        assignees={assignees}
                        isRescheduling={reschedulingTicketId === ticket.id}
                        isTogglingComplete={togglingCompleteTicketId === ticket.id}
                        onEdit={() => onEditTicket?.(ticket)}
                        onDuplicate={() => onDuplicateTicket?.(ticket)}
                        onReschedule={() => onRescheduleTicket?.(ticket)}
                        onToggleComplete={() => onToggleComplete?.(ticket)}
                        onDismiss={() => onDismissTicket?.(ticket)}
                      />
                    ))}
                  </InfiniteScroll>
                )}
              </>
            ) : (
              <InfiniteScroll
                error={error}
                hasItems={items.length > 0}
                hasMoreItems={hasMoreItems}
                isLoading={isLoading}
                loadMoreItems={loadMore}
                stackProps={{ spacing: 1 }}
              >
                {isInboxSection
                  ? inboxItems.map((item) => (
                    <InboxItemCard
                      key={item.id}
                      conversation={item}
                      onDismiss={onDismissConversation}
                      type={inboxNameToType(selectedList)}
                    />
                  ))
                  : <DateSeparatedList dateField="createdAt" items={tripRequestItems} />}
              </InfiniteScroll>
            )}
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default MyLeadsView;
